/* Borderline winter 26/27 · collection page behaviour (P5: sections/bl-collection.liquid, assets/bl-collection.css).
   Prototype v83 winter-proto/assets/js/page-collection.js, on Horizon's machinery:
   - filtering, sorting and the section re-render stay Horizon's (assets/facets.js: facets-form-component and friends);
     this file only opens and closes the dropdowns (one at a time, outside click / Escape / Done; below 990 they are
     bottom sheets over a scrim, CSS only);
   - <bl-results-list> is Horizon's PaginatedList (infinite scroll: the next page's cards come as section HTML and are
     appended); the end line ("Showing 30 of 96 products" → "Showing all 96 products") follows the cards in the grid;
   - compare: a chip on each card adds the product's record (the JSON next to it) to localStorage 'bl-compare' (three
     at most, the oldest drops out); the ink tray shows the picks, the sheet compares them; Add uses the card's own
     add-to-cart (P0's bl-product-card.addVariant) or opens the product when its card is not on this page.
   Section re-renders replace the cards (morph), so every state is re-applied from storage on each DOM change. */
import { Component } from '@theme/component';
import PaginatedList from '@theme/paginated-list';
import '@theme/view-event-elements';

const CMP_KEY = 'bl-compare';
const CMP_MAX = 3;

/** Horizon's paginated list, unchanged, under the bl name (the grid's infinite scroll). */
class BlResultsList extends PaginatedList {}

if (!customElements.get('bl-results-list')) {
  customElements.define('bl-results-list', BlResultsList);
}

/**
 * @typedef {object} CompareRecord
 * @property {number} id
 * @property {string} u - product URL
 * @property {string} t - name
 * @property {string} v - vendor
 * @property {string} i - picture URL
 * @property {string} p - price (formatted)
 * @property {boolean} from - the price varies
 * @property {string} was - compare-at price (formatted) or ''
 * @property {string} s - subtype
 * @property {number | string} f - flex
 * @property {string} fs - '10' (flex_1_10) or 'index' (flex_index)
 * @property {string} l - ability
 * @property {string} z - sizes line
 * @property {string} st - stores with stock
 * @property {number | string} r - rating
 * @property {number | string} rn - rating count
 * @property {number | string} va - first available variant id ('' when sold out)
 * @property {string} vt - that variant's title ('' for a single-variant product)
 */

const compareStore = {
  /** @returns {CompareRecord[]} */
  read() {
    try {
      const list = JSON.parse(localStorage.getItem(CMP_KEY) || '[]');
      return Array.isArray(list) ? list.filter((record) => record && record.id) : [];
    } catch (error) {
      return [];
    }
  },
  /** @param {CompareRecord[]} list */
  write(list) {
    try {
      localStorage.setItem(CMP_KEY, JSON.stringify(list));
    } catch (error) {
      /* storage blocked: the tray still works for this page view through the DOM */
    }
  },
};

class BlCollection extends Component {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  #frame = 0;

  /** @type {string | null} */
  #trayKey = null;

  /** @type {Record<string, string>} */
  #words = {};

  /** @type {Element | null} */
  #lastFocus = null;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    try {
      this.#words = JSON.parse(this.dataset.i18n || '{}');
    } catch (error) {
      this.#words = {};
    }

    document.documentElement.classList.add('bl-coll-js');
    this.addEventListener('toggle', this.#onToggle, { capture: true, signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    document.addEventListener('keydown', this.#onKeydown, { signal });
    document.addEventListener('pointerdown', this.#onOutside, { capture: true, signal });
    window.addEventListener(
      'storage',
      (event) => {
        if (event.key !== CMP_KEY) return;
        this.#trayKey = null;
        this.#sync();
      },
      { signal }
    );

    this.#observer?.disconnect();
    this.#observer = new MutationObserver(this.#schedule);
    this.#observer.observe(this, { childList: true, subtree: true });

    this.#trayKey = null;
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
    this.#observer?.disconnect();
    this.#observer = null;
    cancelAnimationFrame(this.#frame);
    document.documentElement.classList.remove('bl-cmp-open');
  }

  /** @param {string} key */
  #t(key) {
    return this.#words[key] ?? key;
  }

  /* ---------- the dropdowns ---------- */

  /** One panel at a time. `toggle` does not bubble, so this listens in the capture phase. */
  #onToggle = (/** @type {Event} */ event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.matches('[data-bl-facet]') || !details.open) return;
    for (const other of this.querySelectorAll('details[data-bl-facet][open]')) {
      if (other !== details && other instanceof HTMLDetailsElement) other.open = false;
    }
  };

  /** A press outside an open panel closes it (the scrim is inside the panel and closes it through Done). */
  #onOutside = (/** @type {PointerEvent} */ event) => {
    const target = event.target;
    for (const details of this.querySelectorAll('details[data-bl-facet][open]')) {
      if (!(details instanceof HTMLDetailsElement)) continue;
      if (target instanceof Node && details.contains(target)) continue;
      details.open = false;
    }
  };

  #onKeydown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key !== 'Escape') return;
    const open = this.querySelector('details[data-bl-facet][open]');
    if (!(open instanceof HTMLDetailsElement)) return;
    event.preventDefault();
    this.#close(open);
  };

  /** @param {HTMLDetailsElement} details */
  #close(details) {
    details.open = false;
    const summary = details.querySelector('summary');
    if (summary instanceof HTMLElement) summary.focus();
  }

  /* ---------- clicks: Done, compare tray and sheet ---------- */

  #onClick = (/** @type {MouseEvent} */ event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const done = target.closest('[data-bl-done]');
    if (done) {
      const details = done.closest('details');
      if (details instanceof HTMLDetailsElement) this.#close(details);
      return;
    }

    if (target.closest('[data-bl-cmp-open]')) {
      this.#openSheet();
      return;
    }

    if (target.closest('[data-bl-cmp-close]')) {
      this.#closeSheet();
      return;
    }

    if (target.closest('[data-bl-cmp-clear]')) {
      compareStore.write([]);
      this.#sync();
      return;
    }

    const remove = target.closest('[data-bl-cmp-rm]');
    if (remove instanceof HTMLElement) {
      const id = remove.dataset.blCmpRm || '';
      compareStore.write(compareStore.read().filter((record) => String(record.id) !== id));
      this.#sync();
      return;
    }

    const add = target.closest('[data-bl-cmp-add]');
    if (add instanceof HTMLElement) {
      this.#add(add);
      return;
    }

    // a click on the dialog itself (not its box) is the backdrop
    const dialog = this.#dialog;
    if (dialog && target === dialog) this.#closeSheet();
  };

  /* ---------- compare ---------- */

  #onChange = (/** @type {Event} */ event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-bl-cmp]')) return;

    let list = compareStore.read();
    const id = input.dataset.blCmp || '';
    list = list.filter((record) => String(record.id) !== id);
    if (input.checked) {
      const record = this.#record(input);
      if (!record) {
        input.checked = false;
        return;
      }
      list.push(record);
      while (list.length > CMP_MAX) list.shift();
    }
    compareStore.write(list);
    this.#sync();
  };

  /**
   * The record printed next to a card's chip.
   * @param {HTMLInputElement} input
   * @returns {CompareRecord | null}
   */
  #record(input) {
    const script = input.closest('li')?.querySelector('script[data-bl-cmp-data]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent || '');
    } catch (error) {
      return null;
    }
  }

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#sync());
  };

  /** Re-apply every client state after any DOM change (section re-render, appended page, compare change). */
  #sync() {
    const list = compareStore.read();
    const ids = new Set(list.map((record) => String(record.id)));
    for (const input of this.querySelectorAll('input[data-bl-cmp]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const on = ids.has(input.dataset.blCmp || '');
      if (input.checked !== on) input.checked = on;
    }
    this.#renderTray(list);
    this.#renderEnd();
  }

  /** @param {CompareRecord[]} list */
  #renderTray(list) {
    const tray = this.querySelector('[data-bl-tray]');
    if (!(tray instanceof HTMLElement)) return;
    const key = list.map((record) => record.id).join(',');
    if (key === this.#trayKey) return;
    this.#trayKey = key;

    const thumbs = tray.querySelector('[data-bl-tray-thumbs]');
    const text = tray.querySelector('[data-bl-tray-txt]');
    const open = tray.querySelector('[data-bl-cmp-open]');
    thumbs?.replaceChildren(...list.map((record) => this.#thumb(record)));
    if (text) text.textContent = list.length < 2 ? this.#t('pick') : this.#t('selected').replace('{n}', String(list.length));
    if (open instanceof HTMLElement) open.hidden = list.length < 2;
    tray.classList.toggle('is-open', list.length > 0);
    document.documentElement.classList.toggle('bl-cmp-open', list.length > 0);

    const dialog = this.#dialog;
    if (dialog?.open) {
      if (list.length < 2) this.#closeSheet();
      else this.#renderSheet(list);
    }
  }

  /**
   * @param {CompareRecord} record
   * @returns {HTMLElement}
   */
  #thumb(record) {
    const box = document.createElement('div');
    box.className = 'ctray__ph';
    if (record.i) {
      const img = document.createElement('img');
      img.src = record.i;
      img.alt = '';
      img.loading = 'lazy';
      box.append(img);
    }
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'ctray__x';
    remove.dataset.blCmpRm = String(record.id);
    remove.setAttribute('aria-label', `${this.#t('remove')} ${record.t || ''}`.trim());
    remove.textContent = '✕';
    box.append(remove);
    return box;
  }

  /** "Showing 30 of 96 products" until every page is in, then "Showing all 96 products". */
  #renderEnd() {
    const grid = this.querySelector('ul[ref="grid"]');
    const end = this.querySelector('[data-bl-end]');
    if (!(grid instanceof HTMLElement) || !end) return;
    const total = Number(grid.dataset.total) || 0;
    const shown = grid.querySelectorAll(':scope > [ref="cards[]"]').length;
    // sold-out products are skipped server-side, so the last page being in also means "all" (of what is shown)
    const lastPage = grid.dataset.lastPage || '1';
    const lastIn = grid.querySelector(`:scope > [ref="cards[]"][data-page="${lastPage}"]`) !== null;
    const all = shown >= total || lastIn;
    const n = all ? shown : total;
    const noun = this.#t(n === 1 ? 'one' : 'other');
    const text = all
      ? this.#t('all').replace('{n}', String(n)).replace('{noun}', noun)
      : this.#t('some').replace('{x}', String(shown)).replace('{n}', String(total)).replace('{noun}', noun);
    if (end.textContent !== text) end.textContent = text;
  }

  /** @returns {HTMLDialogElement | null} */
  get #dialog() {
    const dialog = this.querySelector('dialog[data-bl-cmp-dialog]');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  #openSheet() {
    const dialog = this.#dialog;
    const list = compareStore.read();
    if (!dialog || list.length < 2) return;
    this.#renderSheet(list);
    this.#lastFocus = document.activeElement;
    dialog.addEventListener(
      'close',
      () => {
        document.documentElement.removeAttribute('scroll-lock');
        if (this.#lastFocus instanceof HTMLElement && this.#lastFocus.isConnected) this.#lastFocus.focus();
      },
      { once: true }
    );
    dialog.showModal();
    document.documentElement.setAttribute('scroll-lock', '');
  }

  #closeSheet() {
    const dialog = this.#dialog;
    if (dialog?.open) dialog.close();
  }

  /** @param {CompareRecord[]} list */
  #renderSheet(list) {
    const body = this.querySelector('[data-bl-cmp-body]');
    if (!body) return;

    const table = document.createElement('table');
    const head = table.createTHead().insertRow();
    head.append(document.createElement('td'));
    for (const record of list) {
      const cell = document.createElement('th');
      cell.scope = 'col';
      const box = document.createElement('div');
      box.className = 'cmp__p';
      const picture = document.createElement('a');
      picture.className = 'ph';
      picture.href = record.u;
      picture.tabIndex = -1;
      picture.setAttribute('aria-hidden', 'true');
      if (record.i) {
        const img = document.createElement('img');
        img.src = record.i;
        img.alt = '';
        picture.append(img);
      }
      const brand = document.createElement('span');
      brand.className = 'cmp__brand';
      brand.textContent = record.v || '';
      const title = document.createElement('div');
      title.className = 'cmp__t';
      const link = document.createElement('a');
      link.href = record.u;
      link.textContent = record.t || '';
      title.append(link);
      box.append(picture, brand, title);
      cell.append(box);
      head.append(cell);
    }

    /** @type {Array<[string, (record: CompareRecord) => string | Node]>} */
    const rows = [
      ['price', (record) => this.#price(record)],
      ['style', (record) => record.s || ''],
      ['flex', (record) => this.#flex(record)],
      ['level', (record) => record.l || ''],
      ['sizes', (record) => record.z || ''],
      ['stock', (record) => record.st || ''],
      ['rating', (record) => this.#rating(record)],
    ];

    const tbody = table.createTBody();
    for (const [key, read] of rows) {
      const values = list.map(read);
      if (values.every((value) => !value)) continue;
      const row = tbody.insertRow();
      const label = document.createElement('th');
      label.scope = 'row';
      label.textContent = this.#t(key);
      row.append(label);
      for (const value of values) {
        const cell = row.insertCell();
        if (value instanceof Node) cell.append(value);
        else cell.textContent = value || '—';
      }
    }

    const actions = tbody.insertRow();
    actions.append(document.createElement('td'));
    for (const record of list) {
      const cell = actions.insertCell();
      if (record.va) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'btn btn--primary btn--sm';
        button.dataset.blCmpAdd = String(record.va);
        button.dataset.product = String(record.id);
        button.dataset.url = record.u;
        button.textContent = record.vt ? `${this.#t('add')} · ${record.vt}` : this.#t('add');
        cell.append(button);
      } else {
        const sold = document.createElement('span');
        sold.className = 'mute';
        sold.textContent = this.#t('sold');
        cell.append(sold);
      }
    }

    body.replaceChildren(table);
  }

  /**
   * @param {CompareRecord} record
   * @returns {string | Node}
   */
  #price(record) {
    if (!record.p) return '';
    const wrap = document.createElement('span');
    wrap.textContent = record.from ? `${this.#t('from')} ${record.p}` : record.p;
    if (record.was) {
      const was = document.createElement('span');
      was.className = 'mute';
      was.textContent = ` · ${this.#t('was')} ${record.was}`;
      wrap.append(was);
    }
    return wrap;
  }

  /** @param {CompareRecord} record */
  #flex(record) {
    if (record.f === '' || record.f == null) return '';
    const value = Number(record.f);
    if (!Number.isFinite(value)) return '';
    if (record.fs !== '10') return String(value);
    const feel = value <= 4 ? this.#t('soft') : value <= 6 ? this.#t('medium') : this.#t('stiff');
    return `${value} / 10 · ${feel}`;
  }

  /** @param {CompareRecord} record */
  #rating(record) {
    if (record.r === '' || record.r == null) return '';
    const value = Number(record.r);
    if (!Number.isFinite(value) || value <= 0) return '';
    return `${value.toFixed(1)} ★${record.rn ? ` (${record.rn})` : ''}`;
  }

  /**
   * Add from the sheet through the card's own add-to-cart (P0), so the cart drawer behaves as everywhere else.
   * @param {HTMLElement} button
   */
  async #add(button) {
    const productId = button.dataset.product || '';
    const variantId = button.dataset.blCmpAdd || '';
    const card = document.querySelector(`bl-product-card[data-product-id="${CSS.escape(productId)}"]`);
    this.#closeSheet();
    if (card && 'addVariant' in card && typeof card.addVariant === 'function') {
      try {
        await card.addVariant(variantId);
      } catch (error) {
        console.error(error);
      }
      return;
    }
    if (button.dataset.url) window.location.assign(button.dataset.url);
  }
}

if (!customElements.get('bl-collection-component')) {
  customElements.define('bl-collection-component', BlCollection);
}
